import client from './client';
import { AuditLog, PaginatedResponse, ListParams } from '../types/index';

export interface AuditLogParams extends ListParams {
  action?: string;
  entityType?: string;
  // Bug #331: filter to one record's own history, so a company or project
  // detail page can show its audit trail. Added to `GET /api/v1/audit` at the
  // same time — it previously filtered on entity_type only.
  entityId?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Bug #331 / WI #323: this module was state 3 — camelCase types, no mapper,
// in both directions. Outbound, `entityType`/`userId`/`dateFrom` were sent
// verbatim and FastAPI (which binds `entity_type`/`user_id`/`start_date`)
// ignored every one of them, so the admin audit page's filters silently did
// nothing. Inbound, every field except `id` and `action` resolved to
// `undefined`.
interface RawAuditLog {
  id: string;
  action: string;
  entity_type: string;
  entity_id?: string | null;
  user_id?: string | null;
  correlation_id?: string | null;
  before_state?: Record<string, unknown> | null;
  after_state?: Record<string, unknown> | null;
  ip_address?: string | null;
  created_at?: string | null;
}

const mapAuditLog = (a: RawAuditLog): AuditLog => ({
  id: a.id,
  action: a.action,
  entityType: a.entity_type,
  entityId: a.entity_id ?? undefined,
  userId: a.user_id ?? undefined,
  // `userName` is on the frontend type but `AuditLogResponse` does not
  // return it — the backend stores a user id only. Left undefined rather
  // than faked; resolving it server-side would be the #330 `client_name`
  // treatment applied to audit, and is not in this item's scope.
  userName: undefined,
  correlationId: a.correlation_id ?? '',
  beforeState: a.before_state ?? undefined,
  afterState: a.after_state ?? undefined,
  ipAddress: a.ip_address ?? undefined,
  createdAt: a.created_at ?? '',
});

interface RawPaginatedAuditLogs {
  items: RawAuditLog[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const toRawAuditParams = (params: AuditLogParams): Record<string, unknown> => {
  const raw: Record<string, unknown> = {};
  if (params.page !== undefined) raw.page = params.page;
  if (params.pageSize !== undefined) raw.page_size = params.pageSize;
  if (params.sortBy !== undefined) raw.sort_by = params.sortBy;
  if (params.sortOrder !== undefined) raw.sort_order = params.sortOrder;
  if (params.action !== undefined) raw.action = params.action;
  if (params.entityType !== undefined) raw.entity_type = params.entityType;
  if (params.entityId !== undefined) raw.entity_id = params.entityId;
  if (params.userId !== undefined) raw.user_id = params.userId;
  if (params.dateFrom !== undefined) raw.start_date = params.dateFrom;
  if (params.dateTo !== undefined) raw.end_date = params.dateTo;
  return raw;
};

export const getAuditLog = async (params: AuditLogParams): Promise<PaginatedResponse<AuditLog>> => {
  const response = await client.get<RawPaginatedAuditLogs>('/audit', {
    params: toRawAuditParams(params),
  });
  const data = response.data;
  return {
    items: (data.items || []).map(mapAuditLog),
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

export const exportAuditLog = async (
  format: 'csv' | 'json',
  params: AuditLogParams = {}
): Promise<Blob> => {
  const response = await client.get('/audit/export', {
    params: { ...toRawAuditParams(params), format },
    responseType: 'blob',
  });
  return response.data;
};
